import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Avatar,
} from '@mui/joy';
import { motion } from 'framer-motion';

import AssignmentRoundedIcon       from '@mui/icons-material/AssignmentRounded';
import CheckCircleRoundedIcon      from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon           from '@mui/icons-material/CancelRounded';
import AccessTimeRoundedIcon       from '@mui/icons-material/AccessTimeRounded';
import LocationOnRoundedIcon       from '@mui/icons-material/LocationOnRounded';
import RuleRoundedIcon             from '@mui/icons-material/RuleRounded';
import ArrowForwardRoundedIcon     from '@mui/icons-material/ArrowForwardRounded';
import CalendarTodayRoundedIcon    from '@mui/icons-material/CalendarTodayRounded';
import PersonRoundedIcon           from '@mui/icons-material/PersonRounded';
import LockOpenRoundedIcon         from '@mui/icons-material/LockOpenRounded';
import SwapHorizRoundedIcon    from '@mui/icons-material/SwapHorizRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon          from '@mui/icons-material/ReceiptLongRounded';
import TrendingUpRoundedIcon           from '@mui/icons-material/TrendingUpRounded';
import HistoryRoundedIcon          from '@mui/icons-material/HistoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import DeleteOutlineRoundedIcon        from '@mui/icons-material/DeleteOutlineRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import BirthdayWidget from '../../components/BirthdayWidget';
import {
    APIGetDashboard,
    APIGetAllAvailability,
    APIGetDayAssignments,
    APIGetPendingChangeRequests,
    APIGetEmployees,
    APIGetPendingFillRequests,
    APIGetPendingSubstitutionRequests,
    APIGetReportToday,
    APIGetEventsUnseenCount,
    APIGetPendingWriteOffRequests,
    APIGetPendingOrderDeletionRequests,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const DAYS_SHORT  = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS_RU   = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getWeekStart = (offset = 0) => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diff + offset * 7);
    mon.setHours(0, 0, 0, 0);
    return mon;
};

const getWeekDays = (start) =>
    Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
    });

const fmtShort = (d) => `${DAYS_SHORT[d.getDay()]} ${d.getDate()}`;
const fmtRange = (days) => `${days[0].getDate()} ${MONTHS_RU[days[0].getMonth()]} — ${days[6].getDate()} ${MONTHS_RU[days[6].getMonth()]}`;

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

// Дни недели в винительном падеже («на что?») по индексу getDay(): 0=Вс … 6=Сб
const WEEKDAY_ACCUSATIVE = ['воскресенье', 'понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу'];
const weekdayAccusative = (date) => WEEKDAY_ACCUSATIVE[date.getDay()];

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ─── Компонент: мини-сетка недели ─────────────────────────────────────────────
// Показывает 7 дней, для каждого — кто доступен, кто назначен
function WeekGrid({ weekDays, availability, assignments, onDayClick, label }) {
    const today = toISO(new Date());

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                    {fmtRange(weekDays)}
                </Typography>
            </Box>

            {/* Дни */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: { xs: 0.5, sm: 1 }, overflow: 'hidden' }}>
                {weekDays.map((date) => {
                    const iso      = toISO(date);
                    const isToday  = iso === today;
                    const isPast   = date < new Date(new Date().setHours(0, 0, 0, 0));
                    const avail    = availability.filter(a => a.date === iso);
                    const assigned = assignments.filter(a => a.date === iso);
                    const filled   = avail.length;
                    const total    = assigned.length;

                    return (
                        <Box
                            key={iso}
                            onClick={() => onDayClick(iso)}
                            sx={{
                                borderRadius: 'var(--joy-radius-md)',
                                border:       '1px solid',
                                borderColor:  isToday
                                    ? 'var(--joy-palette-primary-500)'
                                    : 'var(--joy-palette-divider)',
                                bgcolor:      isToday
                                    ? 'rgba(162,38,226,0.08)'
                                    : isPast
                                        ? 'transparent'
                                        : 'var(--joy-palette-background-level1)',
                                p:            { xs: 0.5, sm: 1 },
                                textAlign:    'center',
                                cursor:       'pointer',
                                opacity:      isPast ? 0.4 : 1,
                                transition:   'all .15s',
                                '&:hover':    !isPast ? {
                                    borderColor: 'var(--joy-palette-primary-400)',
                                    bgcolor:     'rgba(162,38,226,0.06)',
                                } : {},
                            }}
                        >
                            <Typography sx={{
                                fontSize:   9,
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: isToday ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)',
                            }}>
                                {DAYS_SHORT[date.getDay()]}
                            </Typography>
                            <Typography sx={{
                                fontSize:   { xs: 14, sm: 16 },
                                fontWeight: 800,
                                color: isToday ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-primary)',
                                lineHeight: 1.2, my: 0.3,
                            }}>
                                {date.getDate()}
                            </Typography>

                            {/* Доступно / назначено */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
                                {filled > 0 && (
                                    <Box sx={{
                                        fontSize: { xs: 7, sm: 9 }, fontWeight: 700,
                                        color: 'var(--joy-palette-primary-300)',
                                        bgcolor: 'rgba(162,38,226,0.12)',
                                        borderRadius: '4px', px: { xs: 0.3, sm: 0.5 }, py: 0.2,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {filled} дост.
                                    </Box>
                                )}
                                {total > 0 && (
                                    <Box sx={{
                                        fontSize: { xs: 7, sm: 9 }, fontWeight: 700,
                                        color: '#22c55e',
                                        bgcolor: 'rgba(34,197,94,0.1)',
                                        borderRadius: '4px', px: { xs: 0.3, sm: 0.5 }, py: 0.2,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {total} назн.
                                    </Box>
                                )}
                                {filled === 0 && total === 0 && !isPast && (
                                    <Box sx={{ fontSize: 9, color: 'var(--joy-palette-text-tertiary)' }}>—</Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ─── Компонент: строка фотографа (заполнил / не заполнил) ────────────────────
function PhotographerRow({ photographer, availDays, weekDays }) {
    const meta = photographer; // last_updated, days_count, role — теперь в объекте
    const filledIsos = new Set(availDays);
    const filledCount = weekDays.filter(d => filledIsos.has(toISO(d))).length;
    const hasFilled   = filledCount > 0;

    return (
        <Box sx={{
            display:      'flex',
            alignItems:   'center',
            gap:          1.5,
            px:           2,
            py:           1.2,
            borderRadius: 'var(--joy-radius-md)',
            border:       '1px solid var(--joy-palette-divider)',
            bgcolor:      'var(--joy-palette-background-level1)',
        }}>
            <Avatar
                src={photographer.avatar_url}
                size="sm"
                sx={{
                    width: 30, height: 30, fontSize: 11, fontWeight: 700, flexShrink: 0,
                    bgcolor: hasFilled ? 'rgba(162,38,226,0.2)' : 'var(--joy-palette-background-level3)',
                    color:   hasFilled ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)',
                    border:  `1.5px solid ${hasFilled ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)'}`,
                }}
            >
                {(photographer.first_name || '').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Имя + роль */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{
                        fontSize: 13, fontWeight: 600,
                        color: 'var(--joy-palette-text-primary)',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {photographer.first_name} {photographer.last_name}
                    </Typography>
                    {photographer.role === 'admin_photographer' ? (
                        <Chip size="sm" sx={{
                            fontSize: 9, fontWeight: 700, height: 18, px: 0.8,
                            bgcolor: 'rgba(6,182,212,0.1)', color: '#06b6d4',
                        }}>
                            Админ/Фотограф
                        </Chip>
                    ) : photographer.role === 'admin' ? (
                        <Chip size="sm" sx={{
                            fontSize: 9, fontWeight: 700, height: 18, px: 0.8,
                            bgcolor: 'rgba(59,130,246,0.12)', color: '#3b82f6',
                        }}>
                            Админ
                        </Chip>
                    ) : (
                        <Chip size="sm" sx={{
                            fontSize: 9, fontWeight: 700, height: 18, px: 0.8,
                            bgcolor: 'rgba(162,38,226,0.1)', color: 'var(--joy-palette-primary-300)',
                        }}>
                            Фотограф
                        </Chip>
                    )}
                </Box>

                {/* Подпись со счётчиком + когда обновил */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2, flexWrap: 'wrap' }}>
                    <Typography sx={{
                        fontSize: 11,
                        color: hasFilled
                            ? 'var(--joy-palette-primary-400)'
                            : 'var(--joy-palette-text-tertiary)',
                    }}>
                        {hasFilled ? `Заполнил ${filledCount} из 7` : 'Не заполнил'}
                    </Typography>
                    {meta.last_updated && (
                        <Typography sx={{
                            fontSize: 10,
                            color: 'var(--joy-palette-text-tertiary)',
                        }}>
                            · обновлено {new Date(meta.last_updated).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    )}
                </Box>

                {/* 7 рисочек с датами */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0.5,
                    mt: 0.8,
                }}>
                    {weekDays.map(d => {
                        const iso       = toISO(d);
                        const isActive  = filledIsos.has(iso);
                        const dd        = String(d.getDate()).padStart(2, '0');
                        const mm        = String(d.getMonth() + 1).padStart(2, '0');
                        return (
                            <Box key={iso} sx={{
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', gap: 0.3,
                            }}>
                                <Box sx={{
                                    width:  '100%',
                                    height: 4,
                                    borderRadius: 2,
                                    bgcolor: isActive
                                        ? 'var(--joy-palette-primary-500)'
                                        : 'var(--joy-palette-background-level3)',
                                    boxShadow: isActive
                                        ? '0 0 6px rgba(162,38,226,0.55)'
                                        : 'none',
                                    transition: 'all .2s',
                                }} />
                                <Typography sx={{
                                    fontSize: 9,
                                    fontWeight: isActive ? 700 : 500,
                                    color: isActive
                                        ? 'var(--joy-palette-primary-300)'
                                        : 'var(--joy-palette-text-tertiary)',
                                    letterSpacing: '0.02em',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {dd}.{mm}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {hasFilled
                ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#22c55e', flexShrink: 0, alignSelf: 'flex-start', mt: 0.3 }} />
                : <CancelRoundedIcon     sx={{ fontSize: 18, color: 'var(--joy-palette-danger-400)', flexShrink: 0, alignSelf: 'flex-start', mt: 0.3 }} />
            }
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  ManagerDashboard
// ════════════════════════════════════════════════════════════════════
export default function ManagerDashboard() {
    const { token, showToast } = useContext(AppContext);
    const { hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const thisWeekDays = getWeekDays(getWeekStart(0));
    const nextWeekDays = getWeekDays(getWeekStart(1));
    const allDays      = [...thisWeekDays, ...nextWeekDays];

    // Показывать «Следующую неделю» только в субботу и воскресенье
    const isWeekend = (() => {
        const day = new Date().getDay();
        return day === 6 || day === 0;
    })();

    const [availability,     setAvailability]     = useState([]);
    const [assignments,      setAssignments]       = useState([]);
    const [photographers,    setPhotographers]     = useState([]);
    const [pendingRequests,  setPendingRequests]   = useState([]);
    const [pendingSubstitutions, setPendingSubstitutions] = useState([]);
    const [pendingFillRequests, setPendingFillRequests] = useState([]);
    const [eventsUnseenCount, setEventsUnseenCount] = useState(0);
    const [loading,          setLoading]           = useState(true);
    const [todaySummary,     setTodaySummary]      = useState(null);

    const [pendingWriteOffs, setPendingWriteOffs] = useState([]);
    const [pendingOrderDeletions, setPendingOrderDeletions] = useState([]);

    // Мета-данные по фотографам из availability (last_updated, days_count)
    const availMeta = new Map();

    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            const thisWeekIso = toISO(thisWeekDays[0]);
            const nextWeekIso = toISO(nextWeekDays[0]);

            const [availThis, availNext, empl, pending, pendingFill, pendingSubs, todayReport] = await Promise.all([
                APIGetAllAvailability(token, thisWeekIso),
                APIGetAllAvailability(token, nextWeekIso),
                APIGetEmployees(token, 'schedule'),
                APIGetPendingChangeRequests(token),
                APIGetPendingFillRequests(token),
                APIGetPendingSubstitutionRequests(token),
                APIGetReportToday(token),
            ]);

            // Загрузить назначения на все 14 дней
            const assignResults = await Promise.all(
                allDays.map(d => APIGetDayAssignments(token, toISO(d)))
            );
            const allAssign = assignResults.flat().filter(Boolean);

            // Нормализуем ответ APIGetAllAvailability в плоский массив:
            //   { photographers: [{ account: {id, name}, days: [{date, preferred_time, is_locked, ...}] }] }
            //   → [{ account: id, date, preferred_time, is_locked, ... }, ...]
            const normalizeAvail = (resp) => {
                const out = [];
                if (Array.isArray(resp)) {
                    resp.forEach(a => out.push({
                        ...a,
                        account: typeof a.account === 'object' ? a.account?.id : a.account,
                    }));
                } else if (resp && Array.isArray(resp.photographers)) {
                    resp.photographers.forEach(group => {
                        const accId = group.account?.id ?? group.account;
                        (group.days || []).forEach(d => out.push({ ...d, account: accId }));
                    });
                }
                return out;
            };

            const flatAvail = [
                ...normalizeAvail(availThis),
                ...normalizeAvail(availNext),
            ];

            // На всякий случай дедупликация по (account, date) — на случай если бэк вернёт пересечение
            const dedupMap = new Map();
            flatAvail.forEach(a => dedupMap.set(`${a.account}|${a.date}`, a));
            const flatAvailUnique = Array.from(dedupMap.values());

            const extractMeta = (resp) => {
                if (resp && Array.isArray(resp.photographers)) {
                    resp.photographers.forEach(group => {
                        const accId = group.account?.id ?? group.account;
                        availMeta.set(accId, {
                            last_updated: group.last_updated || null,
                            days_count: group.days_count ?? (group.days || []).length,
                        });
                    });
                }
            };
            extractMeta(availThis);
            extractMeta(availNext);
            
            const unseenEvents = hasPerm('events.view')
                ? await APIGetEventsUnseenCount(token)
                : { count: 0 };

            if (Array.isArray(empl))    setPhotographers(empl);
            if (Array.isArray(pending)) setPendingRequests(pending);
            if (Array.isArray(pendingFill)) setPendingFillRequests(pendingFill);
            if (Array.isArray(pendingSubs)) setPendingSubstitutions(pendingSubs);
            // На дашборде показываем только важные/критичные (info-шум не интересен)
            const importantTotal = (unseenEvents?.critical ?? 0) + (unseenEvents?.important ?? 0);
            setEventsUnseenCount(importantTotal);
            setAvailability(flatAvailUnique);
            setAssignments(allAssign);
            if (todayReport) setTodaySummary(todayReport);
            setLoading(false);
            return true;
        } catch (e) { 
            console.error('loadAll:', e); 
            setLoading(false); return false; 
        }
    }, [token]);  // eslint-disable-line

    const { onRefreshTick } = useContext(AppContext);  // если ещё не берёшь из контекста

    useEffect(() => onRefreshTick(loadAll), [onRefreshTick]);

    useEffect(() => { loadAll(); }, [loadAll]);

    useEffect(() => {
        if (!hasPerm('finance.stock.writeoff.review')) return;
        (async () => {
            const res = await APIGetPendingWriteOffRequests(token);
            setPendingWriteOffs(Array.isArray(res) ? res : []);
            if (hasPerm('finance.orders.delete_requests.review')) {
                const del = await APIGetPendingOrderDeletionRequests(token);
                setPendingOrderDeletions(Array.isArray(del) ? del : []);
            }
        })();
    }, [token]);

    // ── Стат-карточки ─────────────────────────────────────────────
    // В субботу/воскресенье считаем по следующей неделе,
    // в остальные дни — по текущей
    const relevantWeekIsos = new Set(
        (isWeekend ? nextWeekDays : thisWeekDays).map(toISO)
    );
    const filledCount = photographers.filter(p =>
        availability.some(a => a.account === p.id && relevantWeekIsos.has(a.date))
    ).length;
    const notFilledCount = photographers.length - filledCount;

    const handleDayClick = (iso) => {
        navigate(`/manager/assign?date=${iso}`);
    };

    // Доступность по неделям
    const availForWeek = (days) => {
        const isos = new Set(days.map(toISO));
        return availability.filter(a => isos.has(a.date));
    };
    const assignForWeek = (days) => {
        const isos = new Set(days.map(toISO));
        return assignments.filter(a => isos.has(a.date));
    };

    // Порядок ролей в графике: админы → админ-фотографы → фотографы
    const rolePriority = (r) => (r === 'admin' ? 0 : r === 'admin_photographer' ? 1 : 2);

    // Фотографы для текущей недели
    const photographersWithAvailThisWeek = photographers.map(p => ({
        ...p,
        availDays: availability
            .filter(a => a.account === p.id && thisWeekDays.map(toISO).includes(a.date))
            .map(a => a.date),
        last_updated: availMeta.get(p.id)?.last_updated || null,
        days_count: availMeta.get(p.id)?.days_count || 0,
    })).sort((a, b) => {
        // Сначала по роли: админы → админ-фотографы → фотографы
        const rp = rolePriority(a.role) - rolePriority(b.role);
        if (rp !== 0) return rp;
        // Внутри роли — заполнившие наверху, среди них последний обновивший первым
        const aFilled = a.availDays.length > 0 ? 1 : 0;
        const bFilled = b.availDays.length > 0 ? 1 : 0;
        if (aFilled !== bFilled) return bFilled - aFilled;
        if (a.last_updated && b.last_updated) return new Date(b.last_updated) - new Date(a.last_updated);
        if (a.last_updated) return -1;
        if (b.last_updated) return 1;
        return 0;
    });

    const photographersWithAvailNextWeek = photographers.map(p => ({
        ...p,
        availDays: availability
            .filter(a => a.account === p.id && nextWeekDays.map(toISO).includes(a.date))
            .map(a => a.date),
        last_updated: availMeta.get(p.id)?.last_updated || null,
        days_count: availMeta.get(p.id)?.days_count || 0,
    })).sort((a, b) => {
        const rp = rolePriority(a.role) - rolePriority(b.role);
        if (rp !== 0) return rp;
        const aFilled = a.availDays.length > 0 ? 1 : 0;
        const bFilled = b.availDays.length > 0 ? 1 : 0;
        if (aFilled !== bFilled) return bFilled - aFilled;
        if (a.last_updated && b.last_updated) return new Date(b.last_updated) - new Date(a.last_updated);
        if (a.last_updated) return -1;
        if (b.last_updated) return 1;
        return 0;
    });

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                <BirthdayWidget />

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Дашборд
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                {isWeekend ? 'Обзор следующей недели' : 'Обзор текущей недели'}
                            </Typography>
                        </Box>
                        {hasPerm('schedule.edit') && (
                            <Button
                                startDecorator={<AssignmentRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => navigate('/manager/assign')}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Назначить смены
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Стат-карточки ──────────────────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5 }}>
                                    {([
                                        hasPerm('schedule.view') && { label: 'Заполнили график',  value: filledCount,    icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />, color: '#22c55e', bg: 'rgba(34,197,94,0.08)' },
                                        hasPerm('schedule.view') && { label: 'Не заполнили',      value: notFilledCount, icon: <CancelRoundedIcon      sx={{ fontSize: 18 }} />, color: 'var(--joy-palette-danger-400)', bg: 'rgba(248,113,113,0.08)' },
                                        hasPerm('schedule.view') && { label: 'Назначений',        value: assignments.length, icon: <AssignmentRoundedIcon sx={{ fontSize: 18 }} />, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)' },
                                        // Заявки от админов на отмену чека
                                        hasPerm('finance.orders.delete_requests.review') && {
                                            label: 'Заявки на отмену',
                                            value: pendingOrderDeletions.length,
                                            icon: <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />,
                                            color: 'var(--joy-palette-danger-400)',
                                            bg: 'rgba(248,113,113,0.08)',
                                            onClick: () => navigate('/manager/order-deletion-requests'),
                                        },
                                        hasPerm('schedule.change_requests.review') && {
                                            label: 'Запросов на изм.', value: pendingRequests.length,
                                            icon: <RuleRoundedIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
                                            onClick: () => navigate('/manager/change-requests'),
                                        },
                                        hasPerm('finance.stock.writeoff.review') && {
                                            label: 'Заявок на списание', value: pendingWriteOffs.length,
                                            icon: <Inventory2RoundedIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
                                            onClick: () => navigate('/manager/write-offs'),
                                        },
                                        hasPerm('schedule.fill_requests.review') && {
                                            label: 'Заявок на доступ',  value: pendingFillRequests.length,
                                            icon: <LockOpenRoundedIcon sx={{ fontSize: 18 }} />, color: '#3b82f6', bg: 'rgba(59,130,246,0.08)',
                                            onClick: () => navigate('/manager/fill-requests'),
                                        },
                                        hasPerm('schedule.substitutions.review') && {
                                            label: 'Заявок на замену',  value: pendingSubstitutions.length,
                                            icon: <SwapHorizRoundedIcon sx={{ fontSize: 18 }} />, color: '#06b6d4', bg: 'rgba(6,182,212,0.08)',
                                            onClick: () => navigate('/manager/substitutions'),
                                        },
                                        hasPerm('events.view') && {
                                            label: 'Новых событий',
                                            value: eventsUnseenCount,
                                            icon: <HistoryRoundedIcon sx={{ fontSize: 18 }} />,
                                            color: eventsUnseenCount > 0 ? '#a78bfa' : 'var(--joy-palette-text-tertiary)',
                                            bg:    eventsUnseenCount > 0 ? 'rgba(167,139,250,0.08)' : 'var(--joy-palette-background-level2)',
                                            onClick: () => navigate('/manager/history'),
                                        },
                                    ].filter(Boolean).map((stat) =>
                                        <Box
                                            key={stat.label}
                                            onClick={stat.onClick}
                                            sx={{
                                                px: 2, py: 1.8,
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: stat.bg,
                                                cursor: stat.onClick ? 'pointer' : 'default',
                                                transition: 'all .15s',
                                                '&:hover': stat.onClick ? { borderColor: stat.color } : {},
                                            }}
                                        >
                                            <Box sx={{ color: stat.color, mb: 0.5 }}>{stat.icon}</Box>
                                            <Typography sx={{ fontSize: 26, fontWeight: 800, color: stat.color, lineHeight: 1 }}>
                                                {stat.value}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                {stat.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                            {/* ── Сводка за сегодня (выручка) ───────────── */}
                            {todaySummary && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Сводка за сегодня
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {todaySummary.date}
                                            </Typography>
                                        </Box>

                                        {/* Общая выручка */}
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 2, py: 1.5, mb: 1.5,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            bgcolor: 'rgba(162,38,226,0.08)',
                                            border: '1px solid var(--joy-palette-divider)',
                                        }}>
                                            <AccountBalanceWalletRoundedIcon sx={{ fontSize: 22, color: 'var(--joy-palette-primary-400)' }} />
                                            <Box>
                                                <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'var(--joy-palette-primary-300)', lineHeight: 1 }}>
                                                    {parseFloat(todaySummary.total_revenue || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2 }}>
                                                    Выручка за день · все точки
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Топ-фотографы */}
                                        {todaySummary.photographers && todaySummary.photographers.length > 0 && (
                                            <Box>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    Фотографы · {todaySummary.photographers.length}
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    {todaySummary.photographers
                                                        .sort((a, b) => parseFloat(b.total || 0) - parseFloat(a.total || 0))
                                                        .slice(0, 5)
                                                        .map((ph, idx) => (
                                                            <Box key={ph.photographer_id || idx} sx={{
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                px: 1.5, py: 0.8,
                                                                borderRadius: 'var(--joy-radius-md)',
                                                                bgcolor: idx === 0 ? 'rgba(34,197,94,0.06)' : 'transparent',
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'var(--joy-palette-text-tertiary)', width: 18 }}>
                                                                        {idx + 1}.
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                                                        {ph.photographer_name}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: idx === 0 ? '#22c55e' : 'var(--joy-palette-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                                                                    {parseFloat(ph.total || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    }
                                                </Stack>
                                            </Box>
                                        )}

                                        {/* Кнопка к полному отчёту */}
                                        <Box
                                            onClick={() => navigate('/manager/stats')}
                                            sx={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                                                mt: 1.5, py: 1,
                                                borderRadius: 'var(--joy-radius-md)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                cursor: 'pointer', transition: 'all .15s',
                                                '&:hover': { borderColor: 'var(--joy-palette-primary-400)', bgcolor: 'rgba(162,38,226,0.04)' },
                                            }}
                                        >
                                            <TrendingUpRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-primary-400)' }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-primary-400)' }}>
                                                Подробная статистика
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}
                            
                            {/* ── Заявки на замену (если есть) ──────────── */}
                            {pendingSubstitutions.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box
                                        onClick={() => navigate('/manager/substitutions')}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            px: 2.5, py: 1.6,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid rgba(6,182,212,0.35)',
                                            bgcolor: 'rgba(6,182,212,0.06)',
                                            cursor: 'pointer', transition: 'all .15s',
                                            '&:hover': { borderColor: 'rgba(6,182,212,0.6)', bgcolor: 'rgba(6,182,212,0.1)' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <SwapHorizRoundedIcon sx={{ fontSize: 20, color: '#06b6d4' }} />
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {pendingSubstitutions.length} заявк{pendingSubstitutions.length > 1 ? 'и' : 'а'} на замену
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Фотографы просят подмену
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Мой график (заполнить свой график как фотограф) ──────────── */}
                            <motion.div variants={fadeUp}>
                                <Box
                                    onClick={() => navigate('/my-schedule')}
                                    sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        px: 2.5, py: 1.6,
                                        borderRadius: 'var(--joy-radius-lg)',
                                        border: '1px solid rgba(162,38,226,0.35)',
                                        bgcolor: 'rgba(162,38,226,0.06)',
                                        cursor: 'pointer', transition: 'all .15s',
                                        '&:hover': { borderColor: 'rgba(162,38,226,0.6)', bgcolor: 'rgba(162,38,226,0.1)' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <AssignmentRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-primary-400)' }} />
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                Мой график
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                Заполнить свой график как фотограф
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                                </Box>
                            </motion.div>

                            {/* ── Запросы на изменение (если есть) ──────────── */}
                            {pendingRequests.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box
                                        onClick={() => navigate('/manager/change-requests')}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            px: 2.5, py: 1.6,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid rgba(245,158,11,0.35)',
                                            bgcolor: 'rgba(245,158,11,0.06)',
                                            cursor: 'pointer', transition: 'all .15s',
                                            '&:hover': { borderColor: 'rgba(245,158,11,0.6)', bgcolor: 'rgba(245,158,11,0.1)' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <RuleRoundedIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {pendingRequests.length} запрос{pendingRequests.length > 1 ? 'а' : ''} на изменение графика
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Требуют вашего решения
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Заявки на заполнение (если есть) ──────────── */}
                            {pendingFillRequests.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box
                                        onClick={() => navigate('/manager/fill-requests')}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            px: 2.5, py: 1.6,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid rgba(59,130,246,0.35)',
                                            bgcolor: 'rgba(59,130,246,0.06)',
                                            cursor: 'pointer', transition: 'all .15s',
                                            '&:hover': { borderColor: 'rgba(59,130,246,0.6)', bgcolor: 'rgba(59,130,246,0.1)' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <LockOpenRoundedIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {pendingFillRequests.length} заявк{pendingFillRequests.length > 1 ? 'и' : 'а'} на доступ к заполнению
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Фотографы просят открыть доступ
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                                    </Box>
                                </motion.div>
                            )}

                            {hasPerm('schedule.view') && (<>
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                        <WeekGrid
                                            label="Текущая неделя"
                                            weekDays={thisWeekDays}
                                            availability={availForWeek(thisWeekDays)}
                                            assignments={assignForWeek(thisWeekDays)}
                                            onDayClick={handleDayClick}
                                        />
                                    </Box>
                                </motion.div>

                                {/* ── Календарная сетка: следующая неделя (только Сб-Вс) ─ */}
                                {isWeekend && (
                                    <motion.div variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            p: { xs: 2, sm: 2.5 },
                                        }}>
                                            <WeekGrid
                                                label="Следующая неделя"
                                                weekDays={nextWeekDays}
                                                availability={availForWeek(nextWeekDays)}
                                                assignments={assignForWeek(nextWeekDays)}
                                                onDayClick={handleDayClick}
                                            />
                                        </Box>
                                    </motion.div>
                                )}
                            </>)}
                            
                            {hasPerm('schedule.view') && (<>
                                {/* ── Кто заполнил (2 колонки) ──────────────────── */}
                                <motion.div variants={fadeUp}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isWeekend ? '1fr 1fr' : '1fr' }, gap: 2 }}>
                                        {/* Текущая неделя */}
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            overflow: 'hidden',
                                        }}>
                                            <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    Текущая неделя
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                    {fmtRange(thisWeekDays)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5 }}>
                                                <Stack spacing={0.8}>
                                                    {photographersWithAvailThisWeek.map(p => (
                                                        <PhotographerRow
                                                            key={p.id}
                                                            photographer={p}
                                                            availDays={p.availDays}
                                                            weekDays={thisWeekDays}
                                                        />
                                                    ))}
                                                    {photographers.length === 0 && (
                                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 2 }}>
                                                            Нет фотографов
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Box>

                                        {/* Следующая неделя (только Сб-Вс) */}
                                        {isWeekend && (
                                            <Box sx={{
                                                bgcolor: 'var(--joy-palette-background-level1)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                borderRadius: 'var(--joy-radius-xl)',
                                                overflow: 'hidden',
                                            }}>
                                                <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        Следующая неделя
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                        {fmtRange(nextWeekDays)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ p: 1.5 }}>
                                                    <Stack spacing={0.8}>
                                                        {photographersWithAvailNextWeek.map(p => (
                                                            <PhotographerRow
                                                                key={p.id}
                                                                photographer={p}
                                                                availDays={p.availDays}
                                                                weekDays={nextWeekDays}
                                                            />
                                                        ))}
                                                        {photographers.length === 0 && (
                                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 2 }}>
                                                                Нет фотографов
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </motion.div>
                            </>)}
                            
                            {/* ── Итого на сегодня и завтра (кто где работает) ── */}
                            {hasPerm('schedule.view') && (() => {
                                const dayCards = [0, 1].map(off => {
                                    const d = new Date(); d.setDate(d.getDate() + off);
                                    const iso = toISO(d);
                                    const dayAssign = assignments.filter(a => a.date === iso);
                                    if (dayAssign.length === 0) return null;

                                    const byLocation = {};
                                    dayAssign.forEach(a => {
                                        const key = a.location_name || '—';
                                        (byLocation[key] = byLocation[key] || []).push(a);
                                    });

                                    const dayNum = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                                    const titleDate = `${weekdayAccusative(d)}, ${dayNum}`;

                                    return (
                                        <Box key={iso} sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            p: { xs: 2, sm: 2.5 },
                                        }}>
                                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-primary-300)', mb: 1.8 }}>
                                                График на {titleDate}
                                            </Typography>
                                            <Stack spacing={2}>
                                                {Object.entries(byLocation).map(([locName, list]) => (
                                                    <Box key={locName}>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.6 }}>
                                                            {locName}
                                                        </Typography>
                                                        <Stack spacing={0.4}>
                                                            {list
                                                                .slice()
                                                                .sort((a, b) => {
                                                                    const ta = a.start_time || '99:99', tb = b.start_time || '99:99';
                                                                    if (ta !== tb) return ta.localeCompare(tb);        // раньше выходят — выше
                                                                    return (b.is_admin ? 1 : 0) - (a.is_admin ? 1 : 0); // в одно время: сначала админы
                                                                })
                                                                .map(a => (
                                                                    <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                                                                        <Avatar src={a.account_avatar_url} size="sm" sx={{ width: 22, height: 22, fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                                                                            {(a.account_name || '').charAt(0).toUpperCase()}
                                                                        </Avatar>
                                                                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-primary)' }}>
                                                                            {a.account_name}
                                                                        </Typography>
                                                                        {a.is_admin && (
                                                                            <Chip size="sm" sx={{
                                                                                bgcolor: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                                                                fontWeight: 700, fontSize: 9, height: 18,
                                                                                border: '1px solid rgba(245,158,11,0.3)',
                                                                            }}>
                                                                                Админ
                                                                            </Chip>
                                                                        )}
                                                                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', ml: 'auto' }}>
                                                                            с {(a.start_time || '').slice(0, 5)}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    );
                                }).filter(Boolean);

                                if (dayCards.length === 0) return null;

                                return (
                                    <motion.div variants={fadeUp}>
                                        <Stack spacing={2}>{dayCards}</Stack>
                                    </motion.div>
                                );
                            })()}
                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}